import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState } from "@/components/app/ui-bits";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AiExtractDialog from "@/components/app/AiExtractDialog";
import RelationsPanel from "@/components/app/RelationsPanel";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import VendorsPanel from "@/components/app/VendorsPanel";
import SubcontractsPanel from "@/components/app/SubcontractsPanel";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";
import ProcurementSourcing from "@/components/app/ProcurementSourcing";
import {
  Handshake, Plus, ArrowRight, FileText, Scales, Truck, Briefcase, Users, ChartBar,
  CheckCircle, XCircle, Lock, FilePdf, Receipt, Buildings, Coins,
  Clock, Warning, Money, ArrowUpRight, ChartLineUp, ListChecks, Package, Storefront,
} from "@phosphor-icons/react";

const STREAM_META = {
  A: { label: "Inbound · Client Contracts", color: "text-emerald-700 bg-emerald-50 border-emerald-300", icon: Buildings },
  B: { label: "Outbound · Goods & Equipment", color: "text-blue-700 bg-blue-50 border-blue-300", icon: Truck },
  C: { label: "Outbound · Sub-consultants", color: "text-purple-700 bg-purple-50 border-purple-300", icon: Briefcase },
  D: { label: "JV & Partnerships", color: "text-amber-700 bg-amber-50 border-amber-300", icon: Users },
};

const PHASE_LABEL = {
  P1: "P1 · Identify", P2: "P2 · Go/No-Go", P3: "P3 · Specify", P4: "P4 · Solicit",
  P5: "P5 · Evaluate", P6: "P6 · Award", P7: "P7 · Execute", P8: "P8 · Close",
};

const TABS = [
  { v: "dashboard", l: "KPI Dashboard",         icon: ChartBar },
  { v: "pipeline", l: "Pipeline (Inbound)",     icon: ChartBar },
  { v: "pre_award", l: "Pre-Award Workflow",    icon: Scales },
  { v: "pr_po", l: "PR / PO (Outbound)",        icon: Truck },
  { v: "sub", l: "Sub-consultants",             icon: Briefcase },
  { v: "jv", l: "JV & Partnerships",            icon: Users },
  { v: "registers", l: "Contract Register",     icon: FileText },
  { v: "subcontracts", l: "Subcontracts",       icon: Scales },
  { v: "suppliers", l: "Vendors & Suppliers",     icon: Receipt },
  { v: "jv_register", l: "JV Register",         icon: Handshake },
];

export default function Procurement() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "dashboard";
  const setTab = (v) => setSearchParams({ tab: v });
  const { user } = useAuth();
  const canEdit = can(user, "procurement.create") ?? hasMinRole(user, "manager");

  const [meta, setMeta] = useState(null);
  const [records, setRecords] = useState([]);
  const [pipeline, setPipeline] = useState({ items: [], totals: {} });
  const [contracts, setContracts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [jvPartners, setJvPartners] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [opsDash, setOpsDash] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createStream, setCreateStream] = useState("A");
  const [detail, setDetail] = useState(null);

  const refresh = async () => {
    try {
      const [m, all, pipe, cr, sup, jvr, kp, od] = await Promise.all([
        api.get("/procurement/meta"),
        api.get("/procurement"),
        api.get("/procurement/views/pipeline"),
        api.get("/procurement/views/contract-register"),
        api.get("/procurement/views/supplier-register"),
        api.get("/procurement/views/jv-register"),
        api.get("/procurement/views/kpis"),
        api.get("/procurement/views/operations-dashboard"),
      ]);
      setMeta(m.data);
      setRecords(all.data.items || []);
      setPipeline(pipe.data);
      setContracts(cr.data.items || []);
      setSuppliers(sup.data.items || []);
      setJvPartners(jvr.data.items || []);
      setKpis(kp.data);
      setOpsDash(od.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, []);

  const byStream = useMemo(() => {
    const buckets = { A: [], B: [], C: [], D: [] };
    for (const r of records) (buckets[r.stream] || []).push(r);
    return buckets;
  }, [records]);

  return (
    <div data-testid="procurement-page">
      <PageHeader
        eyebrow="Operations"
        title="Procurement"
        description="DESCON SOP-aligned · 4 streams · 8-phase universal lifecycle · DOA-driven approvals"
        actions={canEdit && (
          <div className="flex gap-2">
            <AiExtractDialog
              module="subcontractor"
              onSaved={refresh}
              trigger={<Button variant="outline" data-testid="proc-ai-subcontractor-btn"><FilePdf size={14} className="mr-1" /> AI Extract Subcontractor</Button>}
            />
            <AiExtractDialog
              module="procurement"
              onSaved={refresh}
              trigger={<Button variant="outline" data-testid="proc-ai-extract-btn"><FilePdf size={14} className="mr-1" /> AI Extract from RFP</Button>}
            />
            <Button onClick={() => setCreateOpen(true)} className="btn-primary-blue" data-testid="proc-new-btn">
              <Plus size={14} className="mr-1" /> New procurement
            </Button>
          </div>
        )}
      />

      <div className="p-5 md:p-8 space-y-5">
        {kpis && tab !== "dashboard" && <KpiStrip kpis={kpis} totals={pipeline.totals} />}

        <div className="relative rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
          <div className="flex gap-1 overflow-x-auto" style={{ scrollbarWidth: "thin" }}>
            {TABS.map(({ v, l, icon: Icon }) => (
              <button
                key={v}
                onClick={() => setTab(v)}
                className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-3.5 py-2 text-xs font-medium transition ${
                  tab === v ? "bg-[#0f1e42] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
                data-testid={`proc-tab-${v}`}
              >
                <Icon size={14} /> <span>{l}</span>
              </button>
            ))}
          </div>
        </div>

        {tab === "dashboard" && kpis && (
          <OperationsDashboard
            kpis={kpis}
            opsDash={opsDash}
            records={records}
            onOpen={(r) => setDetail(r)}
            onTabChange={setTab}
          />
        )}
        {tab === "pipeline" && <PipelineTab pipeline={pipeline} onOpen={(r) => setDetail(r)} />}
        {tab === "pre_award" && <RecordsTable rows={byStream.A} title="Inbound · Bidding Workflow" onOpen={(r) => setDetail(r)} />}
        {tab === "pr_po" && <RecordsTable rows={byStream.B} title="Outbound · Goods & Equipment" onOpen={(r) => setDetail(r)} />}
        {tab === "sub" && <RecordsTable rows={byStream.C} title="Sub-consultants" onOpen={(r) => setDetail(r)} />}
        {tab === "jv" && <RecordsTable rows={byStream.D} title="JV & Partnerships" onOpen={(r) => setDetail(r)} />}
        {tab === "registers" && <ContractRegisterTab contracts={contracts} onOpen={(id) => setDetail(records.find((r) => r.id === id))} />}
        {tab === "subcontracts" && <SubcontractsPanel canEdit={canEdit} />}
        {tab === "suppliers" && <VendorsPanel />}
        {tab === "jv_register" && <JvRegisterTab rows={jvPartners} />}
      </div>

      <CreateDialog
        open={createOpen} onClose={() => setCreateOpen(false)}
        meta={meta} stream={createStream} setStream={setCreateStream} onCreated={refresh}
      />

      {detail && (
        <RecordDetail
          record={detail} meta={meta} canEdit={canEdit}
          onClose={() => setDetail(null)} onChanged={async () => { await refresh(); setDetail(null); }}
        />
      )}
    </div>
  );
}

// ── Operational Procurement Dashboard ────────────────────────────────────
// Replaces the previous static "Connection Map" with live, action-driven
// widgets: Workflow Summary · Active Requests · Vendor Status · Alerts ·
// Performance KPIs. Cross-module relationships now live INSIDE each record
// (see RelationsPanel inside RecordDetail) — not as static text on the dashboard.

const STREAM_DOT = {
  A: "bg-emerald-500",
  B: "bg-blue-500",
  C: "bg-purple-500",
  D: "bg-amber-500",
};

function OperationsDashboard({ kpis, opsDash, records, onOpen, onTabChange }) {
  if (!opsDash) {
    return <div className="p-10 text-center text-sm text-slate-500" data-testid="proc-dash-loading">Loading procurement dashboard…</div>;
  }
  const ws = opsDash.workflow_summary || {};
  const vs = opsDash.vendor_status || {};
  const perf = opsDash.performance || {};
  const alerts = opsDash.alerts || [];
  const active = opsDash.active_requests || [];

  const openRecord = (id) => {
    const r = records.find((x) => x.id === id);
    if (r) onOpen(r);
  };

  return (
    <div className="space-y-6" data-testid="proc-ops-dashboard">
      {/* 1 · Workflow Summary */}
      <Section
        title="Procurement Workflow Summary"
        subtitle="Live counts across the 8-phase lifecycle. Click a tile to jump to the relevant tab."
        icon={ChartLineUp}
      >
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8" data-testid="proc-workflow-summary">
          <FlowTile testid="flow-pending-requests" icon={ListChecks}  label="Pending requests"    value={ws.pending_requests}        tone="slate"  hint="P1 + P2"  onClick={() => onTabChange("pipeline")} />
          <FlowTile testid="flow-rfqs"             icon={FileText}    label="RFQs in progress"    value={ws.rfqs_in_progress}        tone="blue"   hint="P4 solicit" onClick={() => onTabChange("pipeline")} />
          <FlowTile testid="flow-bids-eval"        icon={Scales}      label="Bids under eval"     value={ws.bids_under_evaluation}   tone="indigo" hint="P5 evaluate" onClick={() => onTabChange("pre_award")} />
          <FlowTile testid="flow-approvals"        icon={Lock}        label="Pending approvals"   value={ws.pending_approvals}       tone={ws.pending_approvals ? "amber" : "slate"} hint="DOA queue" />
          <FlowTile testid="flow-awarded"          icon={CheckCircle} label="Awarded · active"    value={ws.awarded_contracts}       tone="emerald" hint="contracts live" onClick={() => onTabChange("registers")} />
          <FlowTile testid="flow-grns"             icon={Package}     label="Pending GRNs"        value={ws.pending_grns}            tone={ws.pending_grns ? "amber" : "slate"} hint="deliveries to confirm" onClick={() => onTabChange("pr_po")} />
          <FlowTile testid="flow-docs"             icon={Warning}     label="Docs expiring"       value={ws.supplier_docs_expiring + (ws.supplier_docs_expired||0)} tone={ws.supplier_docs_expired ? "red" : (ws.supplier_docs_expiring ? "amber" : "slate")} hint={`${ws.supplier_docs_expired||0} expired`} onClick={() => onTabChange("suppliers")} />
          <FlowTile testid="flow-invoices"         icon={Money}       label="Invoices pending"    value={ws.pending_invoice_match}   tone={ws.pending_invoice_match ? "amber" : "slate"} hint="3-way match" />
        </div>
      </Section>

      {/* 2 · Active Procurement Requests */}
      <Section
        title="Active Procurement Requests"
        subtitle={`${active.length} latest open record${active.length === 1 ? "" : "s"} — click to open.`}
        icon={ListChecks}
        action={<Button size="sm" variant="ghost" className="text-xs text-[#1E5CBA]" onClick={() => onTabChange("pipeline")} data-testid="proc-view-all-requests">View all <ArrowRight size={12} className="ml-1" /></Button>}
      >
        {active.length === 0 ? (
          <EmptyMini>No open procurement requests right now.</EmptyMini>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden lg:block overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-sm" data-testid="proc-active-table">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Ref · Title</th>
                    <th className="px-3 py-2 text-left">Project · Dept</th>
                    <th className="px-3 py-2 text-left">Stream</th>
                    <th className="px-3 py-2 text-right">Value (USD)</th>
                    <th className="px-3 py-2 text-left">Phase</th>
                    <th className="px-3 py-2 text-left">Due</th>
                    <th className="px-3 py-2 text-left">Officer</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {active.map((r) => (
                    <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openRecord(r.id)} data-testid={`active-req-${r.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-900 truncate max-w-[280px]">{r.title}</div>
                        <div className="text-[11px] text-slate-500">{r.ref}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-700">
                        <div className="truncate max-w-[200px]">{r.project_name || "—"}</div>
                        <div className="text-[11px] text-slate-500">{r.department || "—"}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${STREAM_META[r.stream]?.color || "border-slate-300 text-slate-600 bg-slate-50"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${STREAM_DOT[r.stream] || "bg-slate-400"}`}></span>
                          {r.stream}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-mono-alt text-xs">{formatCurrency(r.estimated_value)}</td>
                      <td className="px-3 py-2 text-xs"><PhaseChip phase={r.phase} /></td>
                      <td className="px-3 py-2 text-xs">
                        {r.deadline
                          ? <DueChip days={r.days_to_deadline} date={r.deadline} />
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[140px]">{r.officer || "—"}</td>
                      <td className="px-3 py-2 text-right"><ArrowRight size={14} className="text-slate-400" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="space-y-2 lg:hidden">
              {active.map((r) => (
                <button
                  key={r.id} type="button" onClick={() => openRecord(r.id)}
                  className="w-full rounded-lg border border-slate-200 bg-white p-3 text-left shadow-sm hover:border-[#1E5CBA]"
                  data-testid={`active-req-m-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-slate-900 truncate">{r.title}</div>
                      <div className="text-[11px] text-slate-500">{r.ref} · {r.project_name || "no project"}</div>
                    </div>
                    <PhaseChip phase={r.phase} />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
                    <span className="font-mono-alt">{formatCurrency(r.estimated_value)}</span>
                    {r.deadline && <DueChip days={r.days_to_deadline} date={r.deadline} />}
                    <span className="text-slate-500">· {r.officer || "—"}</span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </Section>

      {/* 3 · Vendor / Supplier Status */}
      <Section
        title="Vendor / Supplier Status"
        subtitle="Master supplier register snapshot — click any tile to drill in."
        icon={Storefront}
        action={<Button size="sm" variant="ghost" className="text-xs text-[#1E5CBA]" onClick={() => onTabChange("suppliers")} data-testid="proc-view-vendors">Open registry <ArrowRight size={12} className="ml-1" /></Button>}
      >
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="proc-vendor-status">
          <VendorTile testid="vendor-total"     label="Total vendors"     value={vs.total}         tone="slate"   />
          <VendorTile testid="vendor-active"    label="Active"            value={vs.active}        tone="emerald" />
          <VendorTile testid="vendor-pre"       label="Pre-selection"     value={vs.pre_selection} tone="blue"    />
          <VendorTile testid="vendor-review"    label="Pending review"    value={vs.pending_review} tone={vs.pending_review ? "amber" : "slate"} />
          <VendorTile testid="vendor-expired"   label="Expired docs"      value={vs.expired_docs}  tone={vs.expired_docs ? "red" : "slate"} />
          <VendorTile testid="vendor-preferred" label="Preferred / AVL"   value={vs.preferred}     tone="emerald" />
        </div>
        {vs.blacklisted > 0 && (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
            <XCircle size={14} className="inline mr-1.5" />
            <strong>{vs.blacklisted}</strong> supplier{vs.blacklisted === 1 ? "" : "s"} blacklisted or suspended — review in the registry.
          </div>
        )}
      </Section>

      {/* 4 · Alerts */}
      <Section
        title="Procurement Alerts"
        subtitle={`${alerts.length} action${alerts.length === 1 ? "" : "s"} require attention.`}
        icon={Warning}
      >
        {alerts.length === 0 ? (
          <EmptyMini>No alerts — everything's on track.</EmptyMini>
        ) : (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2" data-testid="proc-alerts">
            {alerts.map((a, i) => (
              <AlertRow key={i} alert={a} onOpen={openRecord} />
            ))}
          </div>
        )}
      </Section>

      {/* 5 · Performance KPIs */}
      <Section
        title="Procurement Performance"
        subtitle="Month-to-date operational efficiency."
        icon={ChartBar}
      >
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6" data-testid="proc-performance">
          <PerfTile testid="perf-cycle"      icon={Clock}        label="Avg. cycle time"   value={`${perf.avg_cycle_days} d`}             hint="P1 → P6"   tone="blue" />
          <PerfTile testid="perf-completed"  icon={CheckCircle}  label="Completed (MTD)"   value={String(perf.completed_this_month || 0)} hint="awarded"   tone="emerald" />
          <PerfTile testid="perf-value"      icon={Money}        label="Value awarded MTD" value={formatCurrency(perf.value_this_month || 0)} hint="USD"   tone="emerald" />
          <PerfTile testid="perf-pending"    icon={Lock}         label="Pending approvals" value={String(perf.pending_approvals || 0)}    hint="DOA"      tone={perf.pending_approvals ? "amber" : "slate"} />
          <PerfTile testid="perf-compliance" icon={ShieldDot}    label="Supplier compl."   value={`${perf.supplier_compliance_pct || 0}%`} hint="docs valid" tone={(perf.supplier_compliance_pct||0) >= 80 ? "emerald" : "amber"} />
          <PerfTile testid="perf-delayed"    icon={Warning}      label="Delayed"           value={String(perf.delayed_count || 0)}        hint="past deadline" tone={perf.delayed_count ? "red" : "slate"} />
        </div>
        {perf.savings_records > 0 && (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            <Coins size={14} />
            <span>Savings vs. estimate: <strong className="font-mono-alt">{formatCurrency(perf.savings_value)}</strong> across {perf.savings_records} awarded record{perf.savings_records === 1 ? "" : "s"}.</span>
          </div>
        )}
      </Section>

      {/* Existing strategic KPIs — kept (collapsed under one section) */}
      <Section
        title="Strategic KPIs · Annual targets"
        subtitle="Win rate, OTIF, AVL coverage and other long-running indicators against target."
        icon={ChartLineUp}
      >
        {KPI_GROUPS.map((g) => (
          <div key={g.title} className="mb-4 last:mb-0">
            <h4 className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">{g.title}</h4>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
              {g.keys.map((key) => renderKpiTile(kpis, key))}
            </div>
          </div>
        ))}
      </Section>
    </div>
  );
}

// ── Dashboard helper components ──────────────────────────────────────────
const KPI_GROUPS = [
  { title: "Pipeline & Awards",            keys: ["win_rate_pct", "active_inbound", "won_value_ytd", "active_pos", "active_po_value", "pending_approvals"] },
  { title: "Operational Excellence",       keys: ["pr_po_cycle_days", "three_way_match_pct", "sole_source_pct", "supplier_otif_pct", "ncr_count", "avl_coverage_pct"] },
  { title: "Partnerships & Cross-module",  keys: ["active_jv", "active_sub", "assets_auto_created"] },
];

function renderKpiTile(kpis, key) {
  const k = kpis[key];
  if (!k) return null;
  const isPercent = key.endsWith("_pct");
  const isCurrency = key.includes("value") && !key.includes("days");
  const display = isCurrency ? formatCurrency(k.value) : isPercent ? `${k.value}%` : String(k.value);
  let tone = "border-l-slate-400 text-slate-900";
  if (k.target != null) {
    const better = ["sole_source_pct", "ncr_count", "pr_po_cycle_days"].includes(key)
      ? k.value <= k.target : k.value >= k.target;
    tone = better ? "border-l-emerald-500 text-emerald-700" : "border-l-amber-500 text-amber-700";
  }
  return (
    <div key={key} className={`dc-card p-3 border-l-4 ${tone.split(" ")[0]}`} data-testid={`kpi-${key}`}>
      <div className="overline">{k.label}</div>
      <div className={`mt-1 font-display text-xl font-bold tabular-nums ${tone.split(" ")[1] || ""}`}>{display}</div>
      {k.target != null && <div className="text-[10px] text-slate-500 mt-1">target {isPercent ? `${k.target}%` : k.target}</div>}
    </div>
  );
}

const TONE_BORDER = {
  blue:    "border-l-[#1E5CBA]",
  emerald: "border-l-emerald-500",
  amber:   "border-l-amber-500",
  red:     "border-l-rose-500",
  indigo:  "border-l-indigo-500",
  violet:  "border-l-violet-500",
  slate:   "border-l-slate-300",
};

const TONE_TEXT = {
  blue:    "text-[#1E5CBA]",
  emerald: "text-emerald-700",
  amber:   "text-amber-700",
  red:     "text-rose-700",
  indigo:  "text-indigo-700",
  violet:  "text-violet-700",
  slate:   "text-slate-700",
};

function Section({ title, subtitle, icon: Icon, action, children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {Icon && <Icon size={16} className="text-[#1E5CBA]" />}
            <h3 className="font-display text-[15px] font-semibold text-slate-900">{title}</h3>
          </div>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function FlowTile({ testid, icon: Icon, label, value, hint, tone = "slate", onClick }) {
  return (
    <button
      type="button" onClick={onClick} disabled={!onClick}
      data-testid={testid}
      className={`dc-card p-3 border-l-4 ${TONE_BORDER[tone]} text-left transition hover:shadow-md disabled:cursor-default disabled:hover:shadow-none`}
    >
      <div className="flex items-center gap-1.5">
        <Icon size={14} className="text-slate-400" />
        <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 truncate">{label}</div>
      </div>
      <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${TONE_TEXT[tone]}`}>{value ?? 0}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5 truncate">{hint}</div>}
    </button>
  );
}

function VendorTile({ testid, label, value, tone = "slate" }) {
  return (
    <div className={`dc-card p-3 border-l-4 ${TONE_BORDER[tone]}`} data-testid={testid}>
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${TONE_TEXT[tone]}`}>{value ?? 0}</div>
    </div>
  );
}

function PerfTile({ testid, icon: Icon, label, value, hint, tone = "slate" }) {
  return (
    <div className={`dc-card p-3 border-l-4 ${TONE_BORDER[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={14} className="text-slate-400" />}
        <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 truncate">{label}</div>
      </div>
      <div className={`mt-1 font-display text-xl font-semibold tabular-nums ${TONE_TEXT[tone]}`}>{value}</div>
      {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function PhaseChip({ phase }) {
  const tone = phase === "P6" || phase === "P7" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
             : phase === "P4" || phase === "P5" ? "bg-blue-50 text-blue-700 border-blue-200"
             : phase === "P8" ? "bg-slate-100 text-slate-600 border-slate-200"
             : "bg-amber-50 text-amber-700 border-amber-200";
  return (
    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${tone}`}>
      {PHASE_LABEL[phase] || phase || "—"}
    </span>
  );
}

function DueChip({ days, date }) {
  if (days == null) return <span className="text-xs text-slate-500">{formatDate(date)}</span>;
  let tone = "text-slate-600";
  let label = `${days}d`;
  if (days < 0) { tone = "text-rose-700 font-semibold"; label = `${Math.abs(days)}d over`; }
  else if (days <= 3) { tone = "text-amber-700 font-semibold"; }
  return <span className={`text-xs ${tone}`} title={formatDate(date)}>{label}</span>;
}

function AlertRow({ alert, onOpen }) {
  const sev = alert.severity || "blue";
  const tone =
    sev === "red"    ? "border-rose-200 bg-rose-50"   :
    sev === "amber"  ? "border-amber-200 bg-amber-50" :
                       "border-blue-200 bg-blue-50";
  const icon =
    alert.kind === "rfq_deadline"  ? Clock :
    alert.kind === "supplier_doc"  ? Warning :
    alert.kind === "grn_pending"   ? Package :
    alert.kind === "approval_delay"? Lock :
    alert.kind === "invoice_match" ? Money :
    Warning;
  const Icon = icon;
  return (
    <button
      type="button"
      onClick={() => alert.link_id && onOpen(alert.link_id)}
      disabled={!alert.link_id}
      className={`flex w-full items-start gap-3 rounded-lg border ${tone} px-3 py-2 text-left transition hover:shadow-sm disabled:cursor-default`}
      data-testid={`alert-${alert.kind}-${alert.link_id || "x"}`}
    >
      <Icon size={16} className={
        sev === "red" ? "text-rose-600 mt-0.5"
        : sev === "amber" ? "text-amber-600 mt-0.5"
        : "text-blue-600 mt-0.5"
      } />
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm text-slate-900 truncate">{alert.title}</div>
        <div className="text-[11px] text-slate-600">{alert.message}{alert.date && ` · ${alert.date}`}</div>
      </div>
      {alert.link_id && <ArrowUpRight size={14} className="text-slate-400 mt-1 shrink-0" />}
    </button>
  );
}

function EmptyMini({ children }) {
  return <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-500">{children}</div>;
}

// Local lightweight shield icon (avoid extra import noise)
function ShieldDot(props) {
  return (
    <svg viewBox="0 0 24 24" width={props.size || 14} height={props.size || 14} fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}


// ── KPI strip ────────────────────────────────────────────────────────────
function KpiStrip({ kpis, totals }) {
  const card = (label, value, hint, tone = "slate") => (
    <div className={`dc-card p-4 border-l-4 border-l-${tone === "blue" ? "[#1E5CBA]" : tone === "emerald" ? "emerald-500" : tone === "amber" ? "amber-500" : "slate-400"}`}>
      <div className="overline">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold tabular-nums">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
  return (
    <section className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6" data-testid="proc-kpis">
      {card("Win Rate YTD", `${kpis.win_rate_pct.value}%`, `target ${kpis.win_rate_pct.target}%`, "emerald")}
      {card("Active Inbound", kpis.active_inbound.value, "live bids", "blue")}
      {card("Won YTD", formatCurrency(kpis.won_value_ytd.value), null, "emerald")}
      {card("Weighted Pipeline", formatCurrency(totals.weighted_pipeline || 0), `${totals.active || 0} active`, "blue")}
      {card("Active POs", kpis.active_pos.value, formatCurrency(kpis.active_po_value.value), "amber")}
      {card("Pending Approvals", kpis.pending_approvals.value, "in /approvals", "amber")}
    </section>
  );
}

// ── Pipeline tab ─────────────────────────────────────────────────────────
function PipelineTab({ pipeline, onOpen }) {
  if (!pipeline.items?.length) return <EmptyState icon={ChartBar} title="No inbound opportunities yet" description="Click ‘New procurement’ above and select Stream A to log a bid lead." />;
  return (
    <div className="dc-card overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <ChartBar size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Pipeline_Tracker</h3>
        <span className="ml-auto font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">All inbound bids · live</span>
      </div>
      <div className="responsive-table-wrap">
        <table className="data-table w-full min-w-[1100px]">
          <thead><tr><th>Ref</th><th>Client</th><th>Title</th><th>Method</th><th className="text-right">Value</th><th>Phase</th><th>Status</th><th>Go/No-Go</th><th className="text-right">Win %</th><th className="text-right">Weighted</th><th>Days left</th></tr></thead>
          <tbody>
            {pipeline.items.map((r) => (
              <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => onOpen({ id: r.id })} data-testid={`pipe-row-${r.ref}`}>
                <td className="px-5 py-3 font-mono-alt text-xs">{r.ref}</td>
                <td className="px-5 py-3 font-semibold text-slate-900">{r.client}</td>
                <td className="px-5 py-3 text-xs">{r.title}</td>
                <td className="px-5 py-3 font-mono-alt text-[10px] uppercase tracking-widest">{r.method}</td>
                <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{formatCurrency(r.value)}</td>
                <td className="px-5 py-3 text-xs">{PHASE_LABEL[r.phase]}</td>
                <td className="px-5 py-3"><StatusBadge status={r.status === "AWARDED" ? "approved" : r.status === "CLOSED" ? "archived" : "in_progress"} /></td>
                <td className="px-5 py-3 text-xs"><span className={r.go_no_go === "GO" ? "text-emerald-700" : r.go_no_go === "NO-GO" ? "text-red-700" : "text-slate-500"}>{r.go_no_go}</span></td>
                <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{r.win_probability_pct}%</td>
                <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold">{formatCurrency(r.weighted_pipeline)}</td>
                <td className={`px-5 py-3 text-right font-mono-alt text-xs tabular-nums ${(r.days_to_deadline || 0) < 7 && (r.days_to_deadline || 0) >= 0 ? "text-red-600 font-semibold" : "text-slate-500"}`}>{r.days_to_deadline ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Generic records table per stream ─────────────────────────────────────
function RecordsTable({ rows, title, onOpen }) {
  if (!rows?.length) return <EmptyState icon={Handshake} title={`No ${title.toLowerCase()} records yet`} description="Click ‘New procurement’ above to create one." />;
  return (
    <div className="dc-card overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <h3 className="font-display text-[15px] font-semibold text-slate-900">{title}</h3>
        <span className="ml-auto font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{rows.length} record{rows.length === 1 ? "" : "s"}</span>
      </div>
      <div className="responsive-table-wrap">
        <table className="data-table w-full min-w-[900px]">
          <thead><tr><th>Ref</th><th>Counterparty</th><th>Title</th><th className="text-right">Value</th><th>Phase</th><th>Route</th><th>Status</th><th>DOA</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => onOpen(r)} data-testid={`stream-row-${r.ref}`}>
                <td className="px-5 py-3 font-mono-alt text-xs">{r.ref}</td>
                <td className="px-5 py-3 font-semibold text-slate-900">{r.counterparty || "—"}</td>
                <td className="px-5 py-3 text-xs">{r.title}</td>
                <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{formatCurrency(r.estimated_value)}</td>
                <td className="px-5 py-3 text-xs">{PHASE_LABEL[r.phase]}</td>
                <td className="px-5 py-3">
                  {r.route ? (
                    <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${
                      r.route === "fast_track" ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                      : r.route === "standard" ? "border-blue-200 bg-blue-50 text-blue-700"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                    }`}>{r.route_label || r.route}</span>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </td>
                <td className="px-5 py-3"><StatusBadge status={r.status === "AWARDED" ? "approved" : r.status === "CLOSED" ? "archived" : r.status === "ACTIVE" ? "active" : "in_progress"} /></td>
                <td className="px-5 py-3 font-mono-alt text-xs uppercase tracking-widest text-slate-500">{r.approver_tier}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Contract register tab ────────────────────────────────────────────────
function ContractRegisterTab({ contracts, onOpen }) {
  if (!contracts.length) return <EmptyState icon={FileText} title="No contracts in register" description="Submit F-PROC-06 on any awarded record to register a contract." />;
  return (
    <div className="dc-card overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <FileText size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Contract Register · F-PROC-06</h3>
      </div>
      <div className="responsive-table-wrap">
        <table className="data-table w-full min-w-[1000px]">
          <thead><tr><th>Contract #</th><th>Type</th><th>Counterparty</th><th>Project</th><th className="text-right">Value</th><th>Effective</th><th>Expiry</th><th>Status</th><th>Owner</th></tr></thead>
          <tbody>
            {contracts.map((c) => (
              <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => onOpen(c.id)} data-testid={`contract-row-${c.contract_no}`}>
                <td className="px-5 py-3 font-mono-alt text-xs font-semibold">{c.contract_no}</td>
                <td className="px-5 py-3 text-xs">{c.type}</td>
                <td className="px-5 py-3 font-semibold text-slate-900">{c.counterparty}</td>
                <td className="px-5 py-3 text-xs">{c.project}</td>
                <td className="px-5 py-3 text-right font-mono-alt tabular-nums">{formatCurrency(c.value)}</td>
                <td className="px-5 py-3 text-xs">{c.effective_date ? formatDate(c.effective_date) : "—"}</td>
                <td className="px-5 py-3 text-xs">{c.expiry_date ? formatDate(c.expiry_date) : "—"}</td>
                <td className="px-5 py-3"><StatusBadge status={c.status === "AWARDED" ? "approved" : c.status === "ACTIVE" ? "active" : c.status === "CLOSED" ? "archived" : "in_progress"} /></td>
                <td className="px-5 py-3 text-xs text-slate-600">{c.owner || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Supplier register tab (F-PROC-14 master) ────────────────────────────
function SupplierRegisterTab({ rows }) {
  const inspector = useRecordInspector();
  if (!rows.length) return <EmptyState icon={Receipt} title="No suppliers in register yet" description="Submit F-PROC-14 on a Stream B record to enrol a supplier." />;
  return (
    <div className="dc-card overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <Receipt size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">Supplier Register · F-PROC-14</h3>
        <span className="ml-auto font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">{rows.length} suppliers · live AVL status</span>
      </div>
      <div className="responsive-table-wrap">
        <table className="data-table w-full min-w-[900px]" data-testid="supplier-register-table">
          <thead><tr><th>Supplier</th><th>Category</th><th className="text-right">Score</th><th>AVL Status</th><th>Last Evaluated</th><th>Evaluator</th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr
                key={s.id}
                className="cursor-pointer hover:bg-slate-50/70 transition-colors"
                onClick={() => inspector.open(s, { title: s.name || "Supplier", subtitle: `${s.category || "—"} · AVL ${s.avl_status || "—"}` })}
                data-testid={`supplier-row-${s.name}`}
              >
                <td className="px-5 py-3 font-semibold text-slate-900">{s.name}</td>
                <td className="px-5 py-3 text-xs">{s.category || "—"}</td>
                <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold">{s.score?.toFixed(1) ?? "—"}</td>
                <td className="px-5 py-3"><StatusBadge status={s.avl_status === "AVL" ? "approved" : s.avl_status === "WATCH" ? "expiring_soon" : "rejected"} /></td>
                <td className="px-5 py-3 text-xs">{s.last_evaluated ? formatDate(s.last_evaluated) : "—"}</td>
                <td className="px-5 py-3 text-xs text-slate-600">{s.evaluator || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RecordInspector {...inspector.props} />
    </div>
  );
}

// ── JV register tab (F-PROC-18 master) ───────────────────────────────────
function JvRegisterTab({ rows }) {
  const inspector = useRecordInspector();
  if (!rows.length) return <EmptyState icon={Handshake} title="No JV partners in register yet" description="Submit F-PROC-18 on a Stream D record to enrol a partner." />;
  return (
    <div className="dc-card overflow-hidden">
      <div className="border-b border-slate-100 px-5 py-3 flex items-center gap-2">
        <Handshake size={18} weight="fill" style={{ color: "#1E5CBA" }} />
        <h3 className="font-display text-[15px] font-semibold text-slate-900">JV Partner Register · F-PROC-18</h3>
      </div>
      <div className="responsive-table-wrap">
        <table className="data-table w-full min-w-[900px]" data-testid="jv-register-table">
          <thead><tr><th>Partner</th><th>Tier</th><th className="text-right">Score</th><th>Status</th><th>Last Diligence</th></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr
                key={p.id}
                className="cursor-pointer hover:bg-slate-50/70 transition-colors"
                onClick={() => inspector.open(p, { title: p.name || "JV partner", subtitle: `${p.tier || "—"} · ${p.status || "—"}` })}
                data-testid={`jv-row-${p.name}`}
              >
                <td className="px-5 py-3 font-semibold text-slate-900">{p.name}</td>
                <td className="px-5 py-3"><span className={`inline-flex items-center rounded-md border px-2 py-0.5 font-mono-alt text-[10px] uppercase tracking-widest ${p.tier === "Strategic" ? "text-emerald-700 bg-emerald-50 border-emerald-300" : "text-blue-700 bg-blue-50 border-blue-300"}`}>{p.tier}</span></td>
                <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold">{p.score?.toFixed(1) ?? "—"}</td>
                <td className="px-5 py-3"><StatusBadge status={p.status === "Active" ? "approved" : "pending"} /></td>
                <td className="px-5 py-3 text-xs">{p.last_diligence ? formatDate(p.last_diligence) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <RecordInspector {...inspector.props} />
    </div>
  );
}

// ── Create dialog (4 streams) ────────────────────────────────────────────
function CreateDialog({ open, onClose, meta, stream, setStream, onCreated }) {
  const [form, setForm] = useState({ title: "", counterparty: "", estimated_value: 0, description: "" });
  const [preaward, setPreaward] = useState(null);
  const [ack, setAck] = useState(false);
  const [route, setRoute] = useState(null);
  const [category, setCategory] = useState(null);
  const reset = () => { setForm({ title: "", counterparty: "", estimated_value: 0, description: "" }); setPreaward(null); setAck(false); setRoute(null); setCategory(null); };
  useEffect(() => { if (!open) reset(); }, [open]);

  // Phase 4 — value-banded guided routes
  const ROUTE_RANK = { fast_track: 0, standard: 1, full: 2 };
  const thresholds = meta?.route_thresholds || { fast_track_max: 1000, standard_max: 25000 };
  const routes = meta?.routes || [];
  const val = Number(form.estimated_value) || 0;
  const computedRoute = (stream === "A" || stream === "D") ? "full"
    : val <= thresholds.fast_track_max ? "fast_track"
    : val <= thresholds.standard_max ? "standard" : "full";
  useEffect(() => { setRoute(computedRoute); }, [computedRoute]);

  // Pre-award red-flag check — supplier/subcontractor streams only
  useEffect(() => {
    const name = (form.counterparty || "").trim();
    if (stream === "A" || name.length < 3) { setPreaward(null); setAck(false); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/vendors/preaward-check", { params: { name } });
        setPreaward(data); setAck(false);
      } catch { setPreaward(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.counterparty, stream]);

  const criticalFlags = (preaward?.red_flags || []).filter((f) => f.severity === "critical");

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (criticalFlags.length > 0 && !ack) return toast.error("Acknowledge the red flags before proceeding");
    try {
      const body = { stream, ...form, route: route || computedRoute };
      if ((stream === "B" || stream === "C") && category) body.procurement_category = category;
      if (preaward?.found && preaward.vendor_id) body.vendor_id = preaward.vendor_id;
      if (criticalFlags.length > 0) body.extra = { ...(body.extra || {}), preaward_ack: true, preaward_flags: criticalFlags.map((f) => f.code) };
      await api.post("/procurement", body);
      toast.success(`${STREAM_META[stream].label} record created`);
      onClose();
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New procurement record</DialogTitle>
          <DialogDescription>Phase auto-starts at P1 (DRAFT). Approval tier auto-routes per DOA.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Stream</Label>
            <Select value={stream} onValueChange={setStream}>
              <SelectTrigger data-testid="proc-stream-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STREAM_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{k} · {v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={stream === "A" ? "e.g. Mogadishu Drainage Phase II" : "e.g. DJI Matrice 400 + LiDAR"} data-testid="proc-title-input" />
          </div>
          <div>
            <Label>{stream === "A" || stream === "D" ? "Client / Partner" : "Supplier"}</Label>
            <Input value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} placeholder={stream === "A" ? "UNOPS / WB / Govt" : "Vendor name"} data-testid="proc-counterparty-input" />
            {preaward && stream !== "A" && (
              <div className="mt-2 space-y-1.5" data-testid="preaward-panel">
                {preaward.found ? (
                  <>
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-600">
                      <span className="rounded-full bg-blue-50 px-1.5 py-0.5 font-semibold text-blue-700">REGISTERED VENDOR</span>
                      {preaward.avl_status && <span className="rounded-full bg-slate-100 px-1.5 py-0.5">AVL: {preaward.avl_status}</span>}
                      <span>{preaward.summary.engagements_total} engagement(s)</span>
                      {preaward.summary.outstanding_balance > 0 && <span>· outstanding ${preaward.summary.outstanding_balance.toLocaleString()}</span>}
                    </div>
                    {(preaward.red_flags || []).map((f) => (
                      <div key={f.code} className={`rounded-md border px-2.5 py-1.5 text-[11px] font-medium ${
                        f.severity === "critical" ? "border-red-300 bg-red-50 text-red-800"
                        : f.severity === "warning" ? "border-amber-300 bg-amber-50 text-amber-800"
                        : "border-slate-200 bg-slate-50 text-slate-600"}`}
                        data-testid={`preaward-flag-${f.code}`}>
                        ⚠ {f.label}
                      </div>
                    ))}
                    {criticalFlags.length > 0 && (
                      <label className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50/60 px-2.5 py-1.5 text-[11px] font-medium text-red-900">
                        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="preaward-ack" />
                        I acknowledge the critical red flags above and take responsibility for proceeding.
                      </label>
                    )}
                  </>
                ) : (
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-600" data-testid="preaward-new-vendor">
                    {preaward.note}
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <Label>Estimated value (USD)</Label>
            <Input type="number" step="0.01" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: parseFloat(e.target.value || 0) })} data-testid="proc-value-input" />
          </div>
          {(stream === "B" || stream === "C") && (meta?.categories || []).length > 0 && (
            <div>
              <Label>What are you buying?</Label>
              <Select value={category || (stream === "B" ? "goods" : "services")} onValueChange={setCategory}>
                <SelectTrigger data-testid="proc-category-create"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.categories.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}{c.tor_required ? " · TOR required" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-slate-500">
                {(meta.categories.find((c) => c.key === (category || (stream === "B" ? "goods" : "services"))) || {}).hint}
              </p>
            </div>
          )}
          {routes.length > 0 && (
            <div data-testid="proc-route-picker">
              <Label>Procurement route</Label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {routes.map((r) => {
                  const locked = ROUTE_RANK[r.key] < ROUTE_RANK[computedRoute];
                  const selected = (route || computedRoute) === r.key;
                  return (
                    <button
                      key={r.key} type="button" disabled={locked}
                      onClick={() => setRoute(r.key)}
                      data-testid={`proc-route-${r.key}`}
                      title={locked ? `Value $${val.toLocaleString()} requires at least the ${routes.find((x) => x.key === computedRoute)?.label} route` : r.summary}
                      className={`rounded-md border px-2 py-1.5 text-left text-xs transition ${
                        selected ? "border-[#1E5CBA] bg-blue-50 font-semibold text-[#1E5CBA]"
                        : locked ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {r.label}
                      {r.key === computedRoute && (
                        <span className="ml-1 rounded bg-emerald-100 px-1 text-[9px] font-semibold text-emerald-700">SUGGESTED</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="mt-1 text-[11px] text-slate-500" data-testid="proc-route-summary">
                {routes.find((r) => r.key === (route || computedRoute))?.summary}
              </p>
            </div>
          )}
          <div>
            <Label>Description / Scope summary</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <DialogFooter><Button onClick={submit} className="btn-primary-blue" data-testid="proc-create-btn">Create · enter P1 DRAFT</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Record detail drawer ─────────────────────────────────────────────────
function RecordDetail({ record, meta, canEdit, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [advNotes, setAdvNotes] = useState("");
  const [decision, setDecision] = useState("GO");
  const [formCode, setFormCode] = useState(null);

  const load = async () => {
    if (!record?.id) return;
    const { data: d } = await api.get(`/procurement/${record.id}`);
    setData(d);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [record?.id]);

  if (!data) return null;
  const phaseIdx = ["P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8"].indexOf(data.phase);
  const isClosed = data.status === "CLOSED";
  const stream = STREAM_META[data.stream] || STREAM_META.A;

  // Phase 4 — resolve the effective route (stored, or computed for legacy records)
  const th = meta?.route_thresholds || { fast_track_max: 1000, standard_max: 25000 };
  const effRoute = data.route || ((data.stream === "A" || data.stream === "D") ? "full"
    : (data.estimated_value || 0) <= th.fast_track_max ? "fast_track"
    : (data.estimated_value || 0) <= th.standard_max ? "standard" : "full");
  const routeInfo = (meta?.routes || []).find((r) => r.key === effRoute);
  const skipSet = new Set(routeInfo?.skip_phases || []);
  const stepMap = meta?.step_labels?.[data.phase] || {};
  const stepPlain = stepMap[data.stream] || stepMap["*"];

  const advance = async () => {
    try {
      await api.post(`/procurement/${data.id}/advance`, { notes: advNotes, decision });
      toast.success(`Advanced from ${data.phase}`);
      setAdvNotes("");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitForm = async (form_code, payload, autoAdvance = false) => {
    try {
      await api.post(`/procurement/${data.id}/forms`, { form_code, data: payload, auto_advance: autoAdvance });
      toast.success(`${form_code} saved`);
      setFormCode(null);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono-alt text-xs">{data.ref}</span>
            <span>·</span>
            <span className="flex-1">{data.title}</span>
            <AuditTrailDrawer entity="procurement" entityId={data.id} label={`Procurement ${data.ref}`} />
          </DialogTitle>
          <DialogDescription>
            <span className={`inline-flex items-center rounded-md border px-2 py-0.5 font-mono-alt text-[10px] uppercase tracking-widest ${stream.color}`}>{stream.label}</span>
            {routeInfo && (
              <span className={`ml-2 inline-flex items-center rounded-md border px-2 py-0.5 font-mono-alt text-[10px] uppercase tracking-widest ${
                effRoute === "fast_track" ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : effRoute === "standard" ? "border-blue-300 bg-blue-50 text-blue-700"
                : "border-slate-300 bg-slate-50 text-slate-600"
              }`} data-testid="proc-detail-route-badge">{routeInfo.label} route</span>
            )}
            {data.counterparty && <span className="ml-2 text-slate-500">· {data.counterparty}</span>}
          </DialogDescription>
        </DialogHeader>

        {/* Linked-from-Project banner (Phase B) */}
        {(data.deliverable_id && data.project_id) || data.extra?.source === "project_deliverable" ? (
          <div className="flex items-start gap-3 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm">
            <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 font-bold">←</div>
            <div className="min-w-0 flex-1 text-indigo-900">
              <div className="text-[11px] font-mono-alt uppercase tracking-widest text-indigo-700">Linked from Project</div>
              <div className="mt-0.5">
                <span className="font-semibold">{data.project_name || "Project"}</span>
                {data.deliverable_title && (
                  <>
                    <span className="mx-1.5 text-indigo-400">·</span>
                    <span>Deliverable: <span className="font-medium">{data.deliverable_title}</span></span>
                  </>
                )}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] font-mono-alt text-indigo-700">
                {data.phase_id && <span>project phase · {data.phase_id}</span>}
                {data.work_package && <span>work pkg · {data.work_package}</span>}
                {data.requesting_dept && <span>req dept · {data.requesting_dept}</span>}
                {data.requesting_manager && <span>req mgr · {data.requesting_manager}</span>}
              </div>
            </div>
            {data.project_id && (
              <a href={`/admin/projects?focus=${data.project_id}`}
                  className="self-center rounded border border-indigo-300 bg-white px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                  data-testid="open-source-project">
                Open project →
              </a>
            )}
          </div>
        ) : null}

        {/* Phase strip — guided route rail (Phase 4) */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="flex items-center gap-1">
            {meta?.phases?.map((p, i) => {
              const isSkipped = skipSet.has(p.key);
              return (
                <div key={p.key} className="flex-1 flex items-center gap-1 min-w-0">
                  <span
                    className={`flex h-6 items-center rounded px-2 text-[10px] font-mono-alt uppercase tracking-widest ${
                      isSkipped ? "border border-dashed border-slate-300 bg-slate-100 text-slate-400 line-through"
                      : i < phaseIdx ? "bg-emerald-100 text-emerald-700"
                      : i === phaseIdx ? "bg-[#0f1e42] text-white"
                      : "bg-slate-200 text-slate-500"
                    }`}
                    title={isSkipped ? `Not required for the ${routeInfo?.label} route` : (meta?.step_labels?.[p.key]?.[data.stream] || meta?.step_labels?.[p.key]?.["*"] || p.name)}
                    data-testid={`phase-pill-${p.key}`}
                  >
                    {p.key}
                  </span>
                  {i < (meta.phases.length - 1) && <ArrowRight size={10} className="text-slate-300 shrink-0" />}
                </div>
              );
            })}
          </div>
          <div className="mt-1.5 text-xs text-slate-600">
            Currently <span className="font-semibold text-slate-900">{PHASE_LABEL[data.phase]}</span>
            {stepPlain && <> — <span className="font-medium text-[#1E5CBA]" data-testid="proc-step-plain">{stepPlain}</span></>}
            {" "}· status {data.status} · DOA tier <span className="font-mono-alt">{data.approver_tier}</span>
          </div>
          {skipSet.size > 0 && (
            <div className="mt-1 text-[10px] text-slate-500" data-testid="proc-route-skip-note">
              {routeInfo?.label} route — {[...skipSet].join(", ")} not required by value policy (skips are recorded in the phase history &amp; audit log).
            </div>
          )}
        </div>

        {/* Summary grid */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 text-sm">
          <div><div className="overline">Value</div><div className="font-mono-alt tabular-nums font-semibold">{formatCurrency(data.estimated_value)}</div></div>
          <div><div className="overline">Phase</div><div>{PHASE_LABEL[data.phase]}</div></div>
          <div><div className="overline">DOA</div><div className="font-mono-alt">{data.approver_tier}</div></div>
          <div>
            <div className="overline">Project</div>
            <div className="text-xs">
              {data.project_id ? (
                <a href={`/admin/projects?focus=${data.project_id}`}
                   className="text-indigo-700 hover:underline">
                  {data.project_name ? data.project_name.slice(0, 30) : "Linked"}
                </a>
              ) : "—"}
            </div>
          </div>
        </div>

        {/* Sourcing: TOR / spec + quotations + auto-RFQ (streams B & C) */}
        <ProcurementSourcing data={data} canEdit={canEdit && !isClosed} onChanged={() => { load(); onChanged(); }} />

        {/* Forms catalog (filtered by stream) */}
        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-4 py-2 overline">SOP forms applicable</div>
          <div className="divide-y divide-slate-100">
            {(meta?.forms || [])
              .filter((f) => f.stream === "*" || f.stream === data.stream)
              .map((f) => {
                const filed = !!(data.forms && data.forms[f.code]);
                return (
                  <div key={f.code} className="flex items-center gap-3 px-4 py-2 text-sm" data-testid={`form-${f.code}`}>
                    <span className="font-mono-alt text-xs w-24">{f.code}</span>
                    <span className="flex-1 truncate" title={f.name}>
                      {f.plain || f.name}
                      {f.plain && f.plain !== f.name && <span className="ml-1.5 text-[10px] text-slate-400">({f.name})</span>}
                    </span>
                    <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-400">{f.phase}</span>
                    {filed ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle size={12} /> Filed</span>
                    ) : canEdit && !isClosed && (
                      <Button size="sm" variant="outline" onClick={() => setFormCode(f.code)} data-testid={`open-form-${f.code}`}>Fill</Button>
                    )}
                  </div>
                );
              })}
          </div>
        </div>

        {/* Phase advance */}
        {canEdit && !isClosed && (
          <div className="dc-card p-4 space-y-2">
            <div className="overline">Advance phase · gate {data.phase}</div>
            {data.phase === "P2" && (
              <Select value={decision} onValueChange={setDecision}>
                <SelectTrigger data-testid="proc-decision-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="GO">GO — pursue</SelectItem>
                  <SelectItem value="CONDITIONAL">CONDITIONAL</SelectItem>
                  <SelectItem value="NO-GO">NO-GO — close record</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Textarea rows={2} value={advNotes} onChange={(e) => setAdvNotes(e.target.value)} placeholder="Phase notes / gate justification" />
            <div className="flex gap-2">
              <Button onClick={advance} className="btn-primary-blue" data-testid="proc-advance-btn">
                {decision === "NO-GO" && data.phase === "P2" ? <><XCircle size={12} className="mr-1" /> Close record (NO-GO)</> : <>Advance to next phase <ArrowRight size={12} className="ml-1" /></>}
              </Button>
            </div>
          </div>
        )}

        {/* Phase history */}
        <div className="dc-card overflow-hidden">
          <div className="border-b border-slate-100 px-4 py-2 overline">Phase history</div>
          <ul className="divide-y divide-slate-100 text-sm">
            {data.phase_history?.slice().reverse().map((h, i) => (
              <li key={`${h.at}-${i}`} className={`px-4 py-2 flex items-center gap-3 ${h.decision === "SKIPPED-BY-POLICY" ? "bg-slate-50/70 opacity-75" : ""}`}>
                <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500 w-20">{h.phase} · {h.status}</span>
                <span className="flex-1 truncate text-xs text-slate-600">{h.notes}</span>
                {h.decision && (
                  <span className={`text-[10px] font-semibold uppercase tracking-widest ${h.decision === "SKIPPED-BY-POLICY" ? "rounded bg-slate-200 px-1.5 py-0.5 text-slate-500" : "text-slate-700"}`}>
                    {h.decision === "SKIPPED-BY-POLICY" ? "skipped · policy" : h.decision}
                  </span>
                )}
                <span className="font-mono-alt text-[10px] text-slate-400">{formatDate(h.at)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Cross-module connections */}
        <div className="dc-card p-4">
          <div className="mb-3 flex items-center gap-2 overline">
            <ArrowRight size={12} /> Connected records
          </div>
          <RelationsPanel entityType="procurement" entityId={data.id} compact />
        </div>

        {formCode && <FormDialog code={formCode} record={data} onClose={() => setFormCode(null)} onSubmit={submitForm} />}
      </DialogContent>
    </Dialog>
  );
}

// ── Generic form dialog (10 forms — P1+P2 set) ───────────────────────────
function FormDialog({ code, record, onClose, onSubmit }) {
  const [data, setData] = useState({});
  const config = FORM_CONFIGS[code];
  if (!config) return null;
  const set = (k, v) => setData({ ...data, [k]: v });

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle><span className="font-mono-alt text-xs mr-2">{code}</span>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {config.fields.map((f) => (
            <div key={f.k}>
              <Label>{f.l}{f.required && " *"}</Label>
              {f.type === "textarea" ? (
                <Textarea rows={f.rows || 3} value={data[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} />
              ) : f.type === "select" ? (
                <Select value={data[f.k] || ""} onValueChange={(v) => set(f.k, v)}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{f.options.map((o) => <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>)}</SelectContent>
                </Select>
              ) : f.type === "date" ? (
                <Input type="date" value={data[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} />
              ) : f.type === "number" ? (
                <Input type="number" step={f.step || 1} value={data[f.k] || ""} onChange={(e) => set(f.k, parseFloat(e.target.value || 0))} />
              ) : f.type === "checkboxes" ? (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 rounded-md border border-slate-200 p-3">
                  {f.options.map((o) => (
                    <label key={o} className="flex items-start gap-2 text-xs">
                      <Checkbox checked={(data[f.k] || []).includes(o)} onCheckedChange={(ck) => {
                        const arr = data[f.k] || [];
                        set(f.k, ck ? [...arr, o] : arr.filter((x) => x !== o));
                      }} />
                      <span className="leading-tight">{o}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <Input value={data[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onSubmit(code, data, false)} data-testid={`form-save-${code}`}>Save form</Button>
          {config.canAutoAdvance && (
            <Button onClick={() => onSubmit(code, data, true)} className="btn-primary-blue" data-testid={`form-save-advance-${code}`}>
              Save & advance phase <ArrowRight size={12} className="ml-1" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Compact field configs for the 10 P1+P2 forms (essential fields only — full form spec preserved on backend)
const FORM_CONFIGS = {
  "F-PROC-01": {
    title: "Opportunity Capture",
    description: "Stream A · Phase P1 · Log a new bid lead from UNGM/STEP/dgMarket/Direct.",
    canAutoAdvance: true,
    fields: [
      { k: "source_platform", l: "Source platform", type: "select", required: true, options: [["UNGM", "UNGM"], ["dgMarket", "dgMarket"], ["STEP", "STEP"], ["Direct", "Direct"], ["Other", "Other"]] },
      { k: "tender_id", l: "Source URL / Tender ID", required: true },
      { k: "closing_date", l: "Closing date", type: "date", required: true },
      { k: "client_type", l: "Client type", type: "select", options: [["UN", "UN"], ["WB", "WB"], ["AfDB", "AfDB"], ["Govt", "Govt"], ["NGO", "NGO"], ["Private", "Private"]] },
      { k: "country", l: "Country / Location" },
      { k: "method", l: "Method", type: "select", options: [["EOI", "EOI"], ["RFP", "RFP"], ["RFQ", "RFQ"], ["Direct", "Direct"]] },
      { k: "selection_method", l: "Selection method", type: "select", options: [["QCBS", "QCBS"], ["QBS", "QBS"], ["CQS", "CQS"], ["LCS", "LCS"], ["FBS", "FBS"]] },
      { k: "jv_allowed", l: "JV allowed?", type: "select", options: [["Yes", "Yes"], ["No", "No"], ["Required", "Required"]] },
      { k: "scope_summary", l: "Scope summary", type: "textarea", required: true },
      { k: "recommended_action", l: "Recommended action", type: "select", options: [["Pursue", "Pursue"], ["Defer", "Defer"], ["Decline", "Decline"]] },
    ],
  },
  "F-PROC-02": {
    title: "Go/No-Go Scorecard",
    description: "Stream A · Phase P2 · Weighted scoring decision gate.",
    canAutoAdvance: true,
    fields: [
      { k: "strategic_fit", l: "Strategic fit (1-5)", type: "number", step: 1 },
      { k: "past_experience", l: "Past experience (1-5)", type: "number", step: 1 },
      { k: "win_probability_pct", l: "Win probability %", type: "number", step: 1 },
      { k: "risk_flags", l: "Risk flags", type: "checkboxes", options: ["Unfamiliar geography or new client type", "JV with unvetted partner", "Tight deadline (<14 days)", "Below margin threshold", "Sanctions/jurisdiction risk", "Currency / payment risk"] },
      { k: "decision", l: "Decision", type: "select", required: true, options: [["GO", "GO"], ["CONDITIONAL", "CONDITIONAL"], ["NO-GO", "NO-GO"]] },
      { k: "conditions", l: "Conditions (if CONDITIONAL)", type: "textarea" },
    ],
  },
  "F-PROC-03": {
    title: "Bid Compliance Checklist",
    description: "Stream A · Phase P4 · Pre-submission compliance verification.",
    canAutoAdvance: false,
    fields: [
      { k: "submission_method", l: "Submission method", type: "select", options: [["Portal", "Portal"], ["Email", "Email"], ["Hardcopy", "Hardcopy"]] },
      { k: "admin_compliance", l: "Administrative compliance", type: "checkboxes", options: ["Cover letter", "Power of Attorney", "Certificate of Incorporation", "Tax Clearance", "Financial Statements", "ISO 9001", "Bid Security", "JV Agreement"] },
      { k: "tech_compliance", l: "Technical compliance", type: "checkboxes", options: ["Technical Proposal format", "Methodology", "Work Plan", "Org Chart", "Personnel CVs", "Firm experience", "Page limits"] },
      { k: "fin_compliance", l: "Financial compliance", type: "checkboxes", options: ["Currency", "BoQ pricing", "Reimbursables", "Validity period", "Labeling"] },
      { k: "submission_receipt", l: "Submission receipt / acknowledgement #" },
    ],
  },
  "F-PROC-04": {
    title: "Internal Bid Review (Red Team)",
    description: "Stream A · Phase P4 · Independent pre-submission review.",
    canAutoAdvance: false,
    fields: [
      { k: "reviewer_1", l: "Reviewer 1", required: true },
      { k: "reviewer_2", l: "Reviewer 2", required: true },
      { k: "score_total", l: "Total score (out of 100)", type: "number" },
      { k: "top_improvements", l: "Top-3 improvements required", type: "textarea", required: true, rows: 4 },
      { k: "recommendation", l: "Recommendation", type: "select", required: true, options: [["SUBMIT", "SUBMIT"], ["REWORK", "REWORK"], ["WITHDRAW", "WITHDRAW"]] },
    ],
  },
  "F-PROC-05": {
    title: "Award Acceptance / Decline",
    description: "Stream A · Phase P6 · Formal acceptance of Notice of Award.",
    canAutoAdvance: true,
    fields: [
      { k: "noa_date", l: "Notice of Award date", type: "date", required: true },
      { k: "noa_reference", l: "NOA reference", required: true },
      { k: "contract_value", l: "Contract value (USD)", type: "number", required: true, step: 0.01 },
      { k: "contract_duration", l: "Contract duration" },
      { k: "pre_acceptance", l: "Pre-acceptance verification", type: "checkboxes", options: ["NOA terms match bid", "Performance security acceptable", "Insurance caps reviewed", "Key personnel available", "Sub-consultant/JV availability", "Sanctions screening completed"] },
      { k: "decision", l: "Decision", type: "select", required: true, options: [["ACCEPT", "ACCEPT"], ["DECLINE", "DECLINE"], ["NEGOTIATE", "NEGOTIATE"]] },
    ],
  },
  "F-PROC-06": {
    title: "Contract Register Entry",
    description: "Master register · auto-creates Project on Stream A.",
    canAutoAdvance: false,
    fields: [
      { k: "contract_no", l: "Contract # (DESCON)", required: true },
      { k: "contract_value", l: "Contract value (USD)", type: "number", required: true, step: 0.01 },
      { k: "currency", l: "Currency" },
      { k: "effective_date", l: "Effective date", type: "date", required: true },
      { k: "expiry_date", l: "Expiry date", type: "date" },
      { k: "payment_terms", l: "Payment terms", type: "select", options: [["Milestones", "Milestones"], ["Monthly", "Monthly"], ["Lump-Sum", "Lump-Sum"]] },
      { k: "retention_pct", l: "Retention %", type: "number", step: 0.5 },
      { k: "performance_bond", l: "Performance bond (% / amount)" },
      { k: "governing_law", l: "Governing law" },
      { k: "owner", l: "DESCON owner" },
    ],
  },
  "F-PROC-07": {
    title: "Bid Debrief Record",
    description: "Stream A · Phase P8 · Lessons learned (won/lost/withdrawn).",
    canAutoAdvance: false,
    fields: [
      { k: "outcome", l: "Outcome", type: "select", required: true, options: [["WON", "WON"], ["LOST", "LOST"], ["WITHDRAWN", "WITHDRAWN"], ["NO-DECISION", "NO-DECISION"]] },
      { k: "winning_firm", l: "Winning firm (if known)" },
      { k: "what_worked", l: "What worked well?", type: "textarea" },
      { k: "what_did_not", l: "What did not work?", type: "textarea" },
      { k: "biggest_improvement", l: "Single biggest improvement for next time", type: "textarea", required: true },
    ],
  },
  "F-PROC-09": {
    title: "Sole-Source / Direct Selection Justification",
    description: "Required when bypassing competitive bidding · approval one tier above value tier.",
    canAutoAdvance: false,
    fields: [
      { k: "proposed_supplier", l: "Proposed supplier / provider", required: true },
      { k: "justification_basis", l: "Single justification basis", type: "select", required: true, options: [
        ["genuine_emergency", "Genuine emergency"], ["sole_supplier", "Sole supplier"],
        ["compatibility", "Compatibility / standardisation"], ["continuation", "Continuation of ongoing assignment"],
        ["specialised", "Specialised expertise"], ["donor_mandate", "Donor / client mandate"],
        ["below_threshold", "Value below micro-procurement threshold"],
      ] },
      { k: "narrative", l: "Detailed narrative — why competitive cannot be used", type: "textarea", required: true, rows: 4 },
      { k: "vfm_basis", l: "Value-for-money basis (benchmark price evidence)", required: true },
      { k: "sanctions_outcome", l: "Sanctions screening outcome" },
    ],
  },
  "F-PROC-10": {
    title: "Purchase Requisition",
    description: "Stream B · Phase P1 · Initial step for outbound goods/services procurement.",
    canAutoAdvance: true,
    fields: [
      { k: "required_delivery_date", l: "Required delivery date", type: "date", required: true },
      { k: "delivery_location", l: "Delivery location", required: true },
      { k: "items_description", l: "Items / specification", type: "textarea", required: true, rows: 4 },
      { k: "qty", l: "Quantity", type: "number" },
      { k: "uom", l: "Unit of measure" },
      { k: "specifications", l: "Specifications & standards", type: "textarea" },
      { k: "business_justification", l: "Business justification", type: "textarea", required: true },
      { k: "budget_line", l: "Budget line reference", required: true },
      { k: "asset_category", l: "Asset category (if applicable)", type: "select", options: [["CAT-A", "CAT-A · Critical"], ["CAT-B", "CAT-B · Important"], ["CAT-C", "CAT-C · Standard"]] },
    ],
  },
  "F-PROC-12": {
    title: "Goods Received Note (GRN)",
    description: "Stream B · Phase P7 · Verifies receipt against PO · triggers 3-way match · auto-creates Asset record.",
    canAutoAdvance: false,
    fields: [
      { k: "po_number", l: "PO number", required: true },
      { k: "delivery_note_ref", l: "Delivery note / waybill ref", required: true },
      { k: "received_at", l: "Received at (location)" },
      { k: "received_qty", l: "Received quantity", type: "number" },
      { k: "condition", l: "Condition", type: "select", options: [["OK", "OK"], ["Damage", "Damage"], ["Wrong", "Wrong item"]] },
      { k: "inspection_checks", l: "Inspection checks", type: "checkboxes", options: ["Packaging intact", "Quantity matches PO", "Specifications match PO", "Calibration certificate attached", "Manuals present", "Warranty card recorded", "Asset tag affixed"] },
      { k: "ncr_raised", l: "NCR raised (F-PROC-19)?", type: "select", options: [["No", "No"], ["Yes", "Yes"]] },
      { k: "action", l: "Action", type: "select", options: [["Accept", "Accept"], ["Partial Accept", "Partial Accept"], ["Reject", "Reject"], ["Return", "Return"]] },
      { k: "custodian", l: "Custodian assigned" },
      { k: "storage_zone", l: "Storage zone", type: "select", options: [["ZONE-A", "ZONE-A"], ["ZONE-B", "ZONE-B"], ["ZONE-C", "ZONE-C"]] },
    ],
  },
  "F-PROC-08": {
    title: "Conflict-of-Interest Declaration",
    description: "Mandatory pre-evaluation · all evaluators / decision-makers · cross-stream.",
    canAutoAdvance: false,
    fields: [
      { k: "declarant_role", l: "Role in this procurement", type: "select", required: true, options: [["Evaluator","Evaluator"],["Approver","Approver"],["Initiator","Initiator"],["Reviewer","Reviewer"]] },
      { k: "relationship_disclosed", l: "Any relationship with bidders/suppliers/partners?", type: "select", required: true, options: [["No","No, none"],["Yes","Yes — see below"]] },
      { k: "relationship_detail", l: "If yes, describe", type: "textarea", rows: 3 },
      { k: "financial_interest", l: "Any financial interest?", type: "select", options: [["No","No"],["Yes","Yes"]] },
      { k: "recusal_decision", l: "Recusal decision", type: "select", required: true, options: [["No COI","No COI · proceed"],["Recuse","Recuse from this procurement"],["Manage","Manage with safeguards"]] },
    ],
  },
  "F-PROC-11": {
    title: "RFQ / Quotation Evaluation",
    description: "Stream B · Phase P5 · Compares 3 quotes on price + delivery + warranty + AVL.",
    canAutoAdvance: true,
    fields: [
      { k: "vendor_1_name", l: "Vendor 1 — name", required: true },
      { k: "vendor_1_price", l: "Vendor 1 — total price (USD)", type: "number", step: 0.01 },
      { k: "vendor_1_delivery_days", l: "Vendor 1 — delivery (days)", type: "number" },
      { k: "vendor_2_name", l: "Vendor 2 — name" },
      { k: "vendor_2_price", l: "Vendor 2 — total price (USD)", type: "number", step: 0.01 },
      { k: "vendor_2_delivery_days", l: "Vendor 2 — delivery (days)", type: "number" },
      { k: "vendor_3_name", l: "Vendor 3 — name" },
      { k: "vendor_3_price", l: "Vendor 3 — total price (USD)", type: "number", step: 0.01 },
      { k: "vendor_3_delivery_days", l: "Vendor 3 — delivery (days)", type: "number" },
      { k: "evaluation_basis", l: "Evaluation basis", type: "select", options: [["Lowest compliant","Lowest compliant"],["Best value","Best value"],["Sole compliant","Sole compliant"]] },
      { k: "recommended_vendor", l: "Recommended vendor", required: true },
      { k: "vfm_narrative", l: "Value-for-money rationale", type: "textarea", required: true, rows: 3 },
    ],
  },
  "F-PROC-13": {
    title: "3-Way Match · PO ↔ GRN ↔ Invoice",
    description: "Stream B · Phase P7 · Required before payment release.",
    canAutoAdvance: false,
    fields: [
      { k: "po_amount", l: "PO amount (USD)", type: "number", step: 0.01, required: true },
      { k: "grn_amount", l: "GRN value (USD)", type: "number", step: 0.01, required: true },
      { k: "invoice_amount", l: "Invoice amount (USD)", type: "number", step: 0.01, required: true },
      { k: "match_qty", l: "Quantity match?", type: "select", options: [["Match","Match"],["Variance","Variance"]] },
      { k: "match_price", l: "Price match?", type: "select", options: [["Match","Match"],["Variance","Variance"]] },
      { k: "variance_pct", l: "Variance %", type: "number", step: 0.1 },
      { k: "tolerance", l: "Within tolerance (±2% / $50)?", type: "select", required: true, options: [["Yes","Yes"],["No","No"]] },
      { k: "decision", l: "Decision", type: "select", required: true, options: [["RELEASE_PAYMENT","Release payment"],["HOLD","Hold for variance investigation"],["REJECT_INVOICE","Reject invoice"]] },
      { k: "approver", l: "Approver" },
    ],
  },
  "F-PROC-14": {
    title: "Supplier Annual Evaluation",
    description: "Cross-stream · Phase P8 · Drives AVL status (AVL / WATCH / REMOVE).",
    canAutoAdvance: false,
    fields: [
      { k: "evaluation_period", l: "Evaluation period (e.g. 2026 H1)", required: true },
      { k: "delivery_score", l: "Delivery on-time (1-5)", type: "number", step: 1 },
      { k: "quality_score", l: "Quality / specification (1-5)", type: "number", step: 1 },
      { k: "documentation_score", l: "Documentation completeness (1-5)", type: "number", step: 1 },
      { k: "responsiveness", l: "Responsiveness (1-5)", type: "number", step: 1 },
      { k: "ncr_count", l: "NCRs raised against vendor (period)", type: "number" },
      { k: "comments", l: "Comments", type: "textarea", rows: 3 },
      { k: "avl_recommendation", l: "AVL recommendation", type: "select", required: true, options: [["AVL","Keep on AVL"],["WATCH","Place on WATCH"],["REMOVE","Remove from AVL"]] },
    ],
  },
  "F-PROC-15": {
    title: "Sub-consultant Statement of Work (SOW)",
    description: "Stream C · Phase P3 · Defines deliverables, fees, and timeline.",
    canAutoAdvance: true,
    fields: [
      { k: "scope_summary", l: "Scope summary", type: "textarea", required: true, rows: 4 },
      { k: "deliverables", l: "Deliverables (list per line)", type: "textarea", required: true, rows: 4 },
      { k: "duration_days", l: "Duration (days)", type: "number" },
      { k: "fee_basis", l: "Fee basis", type: "select", options: [["Lump-Sum","Lump-Sum"],["Time-Based","Time-Based"],["Mixed","Mixed"]] },
      { k: "fee_amount", l: "Fee amount (USD)", type: "number", step: 0.01, required: true },
      { k: "reimbursables", l: "Reimbursables allowed?", type: "select", options: [["Yes","Yes"],["No","No"]] },
      { k: "ip_clause", l: "IP / deliverable ownership clause", type: "textarea" },
      { k: "key_personnel", l: "Key personnel (names + roles)", type: "textarea" },
    ],
  },
  "F-PROC-16": {
    title: "Sub-consultant Selection",
    description: "Stream C · Phase P5 · Records the technical + financial selection rationale.",
    canAutoAdvance: true,
    fields: [
      { k: "candidates_considered", l: "Candidates considered (number)", type: "number", required: true },
      { k: "shortlist_basis", l: "Shortlist basis", type: "select", options: [["AVL","AVL"],["Open call","Open call"],["Sole-source","Sole-source"]] },
      { k: "tech_score", l: "Recommended candidate technical score", type: "number" },
      { k: "fin_score", l: "Financial score", type: "number" },
      { k: "selected_firm", l: "Selected firm", required: true },
      { k: "fee_negotiated", l: "Negotiated fee (USD)", type: "number", step: 0.01 },
      { k: "rationale", l: "Selection rationale", type: "textarea", required: true, rows: 3 },
    ],
  },
  "F-PROC-17": {
    title: "Sub-consultant Performance Review",
    description: "Stream C · Phase P7/P8 · End-of-engagement scorecard.",
    canAutoAdvance: false,
    fields: [
      { k: "engagement_outcome", l: "Engagement outcome", type: "select", required: true, options: [["Successful","Successful"],["Partial","Partial"],["Unsatisfactory","Unsatisfactory"]] },
      { k: "delivery_score", l: "Delivery quality (1-5)", type: "number" },
      { k: "timeliness_score", l: "Timeliness (1-5)", type: "number" },
      { k: "communication_score", l: "Communication (1-5)", type: "number" },
      { k: "overall_score", l: "Overall (1-5)", type: "number" },
      { k: "would_engage_again", l: "Would engage again?", type: "select", required: true, options: [["Yes","Yes"],["Conditional","Conditional"],["No","No"]] },
      { k: "lessons", l: "Lessons learned", type: "textarea", rows: 3 },
    ],
  },
  "F-PROC-18": {
    title: "JV Partner Scorecard",
    description: "Stream D · Phase P2 · Strategic vs Tactical tier classification.",
    canAutoAdvance: true,
    fields: [
      { k: "tier", l: "Partnership tier", type: "select", required: true, options: [["Strategic","Strategic (multi-year)"],["Tactical","Tactical (single-bid)"]] },
      { k: "geographic_fit", l: "Geographic fit (1-5)", type: "number" },
      { k: "technical_complementarity", l: "Technical complementarity (1-5)", type: "number" },
      { k: "financial_strength", l: "Financial strength (1-5)", type: "number" },
      { k: "compliance_track_record", l: "Compliance / sanctions track record (1-5)", type: "number" },
      { k: "previous_collaboration", l: "Previous collaboration (1-5)", type: "number" },
      { k: "overall_score", l: "Overall score (1-5)", type: "number", required: true },
      { k: "status", l: "Status", type: "select", required: true, options: [["Active","Active"],["Under review","Under review"],["Suspended","Suspended"]] },
      { k: "recommendation", l: "Recommendation", type: "select", options: [["Proceed","Proceed with JV"],["Conditions","Proceed with conditions"],["Decline","Decline"]] },
    ],
  },
  "F-PROC-19": {
    title: "Non-Conformance Record (NCR)",
    description: "Cross-stream · Phase P7 · Captures defects, late delivery, spec mismatch.",
    canAutoAdvance: false,
    fields: [
      { k: "ncr_category", l: "Category", type: "select", required: true, options: [["Quality","Quality"],["Quantity","Quantity"],["Late delivery","Late delivery"],["Spec mismatch","Spec mismatch"],["Documentation","Documentation"],["Safety","Safety"]] },
      { k: "severity", l: "Severity", type: "select", required: true, options: [["Minor","Minor"],["Major","Major"],["Critical","Critical"]] },
      { k: "description", l: "Description of non-conformance", type: "textarea", required: true, rows: 4 },
      { k: "root_cause", l: "Root cause analysis", type: "textarea" },
      { k: "disposition", l: "Disposition", type: "select", required: true, options: [["Rework","Rework"],["Replace","Replace"],["Reject","Reject"],["Concession","Use as-is (concession)"]] },
      { k: "corrective_action", l: "Corrective action required", type: "textarea" },
      { k: "supplier_response_due", l: "Supplier response due (date)", type: "date" },
      { k: "status", l: "NCR status", type: "select", options: [["Open","Open"],["Under investigation","Under investigation"],["Closed","Closed"]] },
    ],
  },
  "F-PROC-20": {
    title: "Asset Registration / Tagging",
    description: "Stream B · Phase P7 · Triggered automatically by GRN Accept (Stream B + asset_category) or filled manually here.",
    canAutoAdvance: false,
    fields: [
      { k: "asset_no", l: "Asset number (auto if blank)" },
      { k: "make_model", l: "Make / model", required: true },
      { k: "serial_no", l: "Serial number", required: true },
      { k: "category", l: "Asset category", type: "select", required: true, options: [["CAT-A","CAT-A · Critical"],["CAT-B","CAT-B · Important"],["CAT-C","CAT-C · Standard"]] },
      { k: "location", l: "Storage location" },
      { k: "custodian", l: "Custodian" },
      { k: "calibration_required", l: "Calibration required?", type: "select", options: [["Yes","Yes"],["No","No"]] },
      { k: "calibration_cycle_months", l: "Calibration cycle (months)", type: "number" },
      { k: "warranty_expiry", l: "Warranty expiry", type: "date" },
      { k: "tag_affixed", l: "Asset tag affixed?", type: "select", options: [["Yes","Yes"],["Pending","Pending"]] },
    ],
  },
};
